import { Colors } from '@amelio/ui-library';

import type { ColorType } from '@amelio/ui-library';

const DEFAULT_TICK_PADDING = 15;
const TICK_COUNT = 7;
const DEFAULT_FALLBACK_MAX_Y_VALUE = 5;

// --- Shared Chart Color Constants ---

export const CHART_COLORS: readonly ColorType[] = [
    Colors.CHART_RED,
    Colors.CHART_ORANGE,
    Colors.CHART_YELLOW,
    Colors.CHART_LIME,
    Colors.CHART_GREEN,
    Colors.CHART_TEAL,
    Colors.CHART_CYAN,
] as const;

export const DARK_TEXT_THRESHOLD_INDEX = 2;
export const MIN_PERCENTAGE = 0;
export const MAX_PERCENTAGE = 100;
export const DEFAULT_Y_AXIS_VALUES = [MIN_PERCENTAGE, 20, 40, 60, 80, MAX_PERCENTAGE];

// --- Shared Badge Helpers ---

export const getBadgeColor = (displayIndex: number): ColorType => {
    const colorIndex = displayIndex % CHART_COLORS.length;

    return CHART_COLORS[colorIndex];
};

export const getBadgeTextColor = (displayIndex: number): ColorType => {
    const shouldUseDarkText = displayIndex >= DARK_TEXT_THRESHOLD_INDEX;

    return shouldUseDarkText ? Colors.BASE : Colors.WHITE;
};

/**
 * Calculate minimum axis tick with padding
 * Ensures the tick doesn't go below the range minimum
 * @param value - The current minimum value
 * @param rangeMin - The absolute minimum (usually 0)
 * @param padding - Padding to subtract from value
 * @returns Padded minimum tick value
 */
export const calculateMinAxisTick = (
    value: number,
    rangeMin: number,
    padding: number = DEFAULT_TICK_PADDING,
): number => {
    const paddedValue = value - padding;
    const shouldUseRangeMin = paddedValue <= rangeMin;

    return shouldUseRangeMin ? rangeMin : paddedValue;
};

/**
 * Calculate maximum axis tick with padding
 * Ensures the tick doesn't exceed the range maximum
 * @param value - The current maximum value
 * @param rangeMax - The absolute maximum (usually 100)
 * @param padding - Padding to add to value
 * @returns Padded maximum tick value
 */
export const calculateMaxAxisTick = (
    value: number,
    rangeMax: number,
    padding: number = DEFAULT_TICK_PADDING,
): number => {
    const paddedValue = value + padding;
    const shouldUseRangeMax = paddedValue >= rangeMax;

    return shouldUseRangeMax ? rangeMax : paddedValue;
};

/**
 * Generate Y-axis values with equal spacing
 * Uses functional programming approach
 * @param minValue - Minimum Y value
 * @param maxValue - Maximum Y value
 * @returns Array of Y-axis values with equal spacing
 */
export const generateYAxisValues = (minValue: number, maxValue: number): number[] => {
    const range = maxValue - minValue;
    const divisor = TICK_COUNT - 1;
    const step = divisor > 0 ? range / divisor : 0;

    return Array.from({ length: TICK_COUNT }, (_, index) => {
        const rawValue = minValue + step * index;
        return Math.round(rawValue);
    });
};

/**
 * Sort percentages in descending order (most picked first)
 * @param a - First percentage object
 * @param b - Second percentage object
 * @returns Sort comparison result
 */
export const sortByPercentageDescending = (
    a: { percentage: number },
    b: { percentage: number },
): number => {
    return b.percentage - a.percentage;
};

interface GetResponseLabelParams {
    count: number;
    singularLabel: string;
    pluralLabel: string;
}

/**
 * Get appropriate response label based on count
 * @param params - Parameters object
 * @param params.count - Number of responses
 * @param params.singularLabel - Label for single response
 * @param params.pluralLabel - Label for multiple responses
 * @returns Appropriate label based on count
 */
export const getResponseLabel = ({
    count,
    singularLabel,
    pluralLabel,
}: GetResponseLabelParams): string => {
    const isSingular = count === 1;

    return isSingular ? singularLabel : pluralLabel;
};

interface FormatTooltipTextParams {
    percentage: number;
    count: number;
    responseLabel: string;
}

/**
 * Format tooltip text with percentage and count
 * @param params - Parameters object
 * @param params.percentage - Percentage value
 * @param params.count - Number of responses
 * @param params.responseLabel - Localized response label
 * @returns Formatted tooltip text
 */
export const formatTooltipText = ({
    percentage,
    count,
    responseLabel,
}: FormatTooltipTextParams): string => {
    return `${percentage}% (${count} ${responseLabel})`;
};

// --- Shared Chart Utilities ---

interface OrderOptionsWithOtherLastParams<T> {
    options: T[];
    hasOtherOption: boolean;
}

/**
 * Order options by reversing regular options and placing "Other" at the end
 * Backend sends "Other" as the last item; this function separates it,
 * reverses the regular options (highest first), and appends "Other" at the end.
 * @param params - Options array and whether the question has an "Other" option
 * @returns Ordered options array (new array, never mutates input)
 */
export const orderOptionsWithOtherLast = <T>({
    options,
    hasOtherOption,
}: OrderOptionsWithOtherLastParams<T>): T[] => {
    const otherOption = hasOtherOption ? options[options.length - 1] : null;
    const regularOptions = hasOtherOption ? options.slice(0, -1) : [...options];

    return [...regularOptions.reverse(), ...(otherOption ? [otherOption] : [])];
};

/**
 * Generate Y-axis values at quarter intervals (0%, 25%, 50%, 75%, 100%)
 * Returns deduplicated and sorted values
 * @param maxValue - Maximum Y-axis value
 * @returns Array of Y-axis values
 */
export const generateQuarterYAxisValues = (maxValue: number): number[] => {
    return Array.from(
        new Set([
            0,
            Math.round(maxValue * 0.25),
            Math.round(maxValue * 0.5),
            Math.round(maxValue * 0.75),
            maxValue,
        ]),
    ).sort((a, b) => a - b);
};

/**
 * Calculate maxYValue from answeredCount or data values
 * Falls back to DEFAULT_FALLBACK_MAX_Y_VALUE when all values are zero
 * @param answeredCount - Total answered count
 * @param dataValues - Array of data values
 * @returns Maximum Y value for the chart
 */
export const calculateMaxYValue = (answeredCount: number, dataValues: number[]): number => {
    return answeredCount || Math.max(...dataValues, 0) || DEFAULT_FALLBACK_MAX_Y_VALUE;
};
